import { useMemo } from "react";

import { Spinner, Typography, Pagination } from "@bigbinary/neeto-atoms";
import { isPresent } from "neetocist";
import { DEFAULT_PAGE_INDEX } from "neetocommons/constants";
import { useQueryParams } from "neetocommons/react-utils";
import { buildUrl } from "neetocommons/utils/general";
import propTypes from "prop-types";
import { mergeLeft } from "ramda";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import Card from "./Card";
import { AUDIT_LOGS_PAGE_SIZE } from "./constants";
import { useFetchAuditLogs } from "./useAuditLogsApi";
import { useFetchCountries } from "./useCountriesApi";
import { useFetchPermissions } from "./usePermissionsApi";
import { indexById, indexByPermissionName } from "./utils";

import Container from "../Container";
import Header from "../Header";

const AuditLogsPagination = ({ count, pageNo, pageSize }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const queryParams = useQueryParams();

  const navigateToPage = page => {
    history.push(buildUrl(pathname, mergeLeft({ page }, queryParams)));
  };

  return (
    <Pagination {...{ count, pageNo, pageSize }} navigate={navigateToPage} />
  );
};

const AuditLogs = ({ headerProps, noDataHelpText, noDataTitle }) => {
  const { t } = useTranslation();

  const { page = DEFAULT_PAGE_INDEX } = useQueryParams();

  const {
    data: { auditLogs = [], totalCount = 0 } = {},
    isLoading,
    isFetching,
  } = useFetchAuditLogs({ params: { page, pageSize: AUDIT_LOGS_PAGE_SIZE } });

  const { data: countriesData } = useFetchCountries();

  const { data: permissionsData } = useFetchPermissions();

  const countriesList = countriesData?.countries;
  const permissionsList = permissionsData?.permissions;

  const permissionsMap = useMemo(
    () => (permissionsList ? indexByPermissionName(permissionsList) : {}),
    [permissionsList]
  );

  const countriesMap = useMemo(
    () => (countriesList ? indexById(countriesList) : {}),
    [countriesList]
  );

  if (isLoading || isFetching) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Container isHeaderFixed className="overflow-hidden">
      <Header
        className="px-5 md:px-10"
        title={t("neetoMolecules.auditLogs.title")}
        {...headerProps}
      />
      {isPresent(auditLogs) ? (
        <div className="w-full grow overflow-y-auto px-5 md:px-10">
          {auditLogs.map((auditLog, index) => (
            <Card
              {...{ auditLog, countriesMap, index, permissionsMap }}
              key={auditLog.id}
              totalCount={auditLogs.length}
            />
          ))}
        </div>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-center">
          <Typography variant="h3" weight="semibold">
            {noDataTitle || t("neetoMolecules.auditLogs.noData")}
          </Typography>
          {noDataHelpText && (
            <Typography asChild variant="body2">
              <p className="text-muted-foreground">{noDataHelpText}</p>
            </Typography>
          )}
        </div>
      )}
      {totalCount > 0 && (
        <div className="flex w-full justify-end px-5 py-4 md:px-10">
          <AuditLogsPagination
            count={totalCount}
            pageNo={parseInt(page)}
            pageSize={AUDIT_LOGS_PAGE_SIZE}
          />
        </div>
      )}
    </Container>
  );
};

AuditLogs.propTypes = {
  /**
   * Props to be passed to the `Header` component.
   */
  headerProps: propTypes.object,
  /**
   * Help text to be displayed when there are no audit logs.
   */
  noDataHelpText: propTypes.string,
  /**
   * Title to be displayed when there are no audit logs.
   */
  noDataTitle: propTypes.string,
};

export default AuditLogs;
